import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';

export interface CaseNoteModalProps {
    isOpen: boolean;
    isReadOnly: boolean;
    noteValue: string;
    onClose: () => void;
    onSave: (value: string) => void | Promise<void>;
    isSaving?: boolean;
    originalValue?: string; // For comparing if value changed (used in HomePage)
    onChange?: (value: string) => void; // Optional onChange for controlled input (used in WizardHeader)
    useUncontrolledInput?: boolean; // true for HomePage (uncontrolled), false for WizardHeader (controlled)
}

/**
 * Case note modal component - can be used for viewing/editing case notes
 * Supports both controlled and uncontrolled input modes
 */
export const CaseNoteModal: React.FC<CaseNoteModalProps> = ({
    isOpen,
    isReadOnly,
    noteValue,
    onClose,
    onSave,
    isSaving = false,
    originalValue,
    onChange,
    useUncontrolledInput = false,
}) => {
    const theme = useTheme();
    const noteInputRef = useRef<HTMLTextAreaElement>(null);
    const counterRef = useRef<HTMLSpanElement>(null);
    const mouseDownOutsideRef = useRef<boolean>(false);
    const MAX_NOTE_LENGTH = 4000;
    const [pasteWarning, setPasteWarning] = useState<string | null>(null);

    // Update character counter directly via DOM (no React state for performance)
    const updateCharacterCounter = useCallback((length: number) => {
        if (!counterRef.current || isReadOnly) return;

        const remaining = MAX_NOTE_LENGTH - length;
        const isNearLimit = remaining < 100;

        const color = isNearLimit
            ? (remaining < 0 ? '#ef4444' : '#f59e0b')
            : theme.colors.text.secondary;
        const fontWeight = isNearLimit ? String(theme.typography.fontWeight.medium) : 'normal';

        const text = remaining >= 0
            ? `${remaining.toLocaleString()} character${remaining !== 1 ? 's' : ''} remaining`
            : `${Math.abs(remaining).toLocaleString()} character${Math.abs(remaining) !== 1 ? 's' : ''} over limit`;

        counterRef.current.textContent = text;
        counterRef.current.style.color = color;
        counterRef.current.style.fontWeight = fontWeight;
    }, [isReadOnly, theme]);

    // Focus textarea when modal opens (if not read-only) and update character count
    useEffect(() => {
        if (isOpen) {
            // Update counter with initial value
            const initialLength = useUncontrolledInput
                ? (noteInputRef.current?.value?.length || noteValue.length)
                : noteValue.length;
            updateCharacterCounter(initialLength);

            if (!isReadOnly && noteInputRef.current) {
                setTimeout(() => {
                    noteInputRef.current?.focus();
                }, 0);
            }
        } else {
            // Clear warning when modal closes
            setPasteWarning(null);
        }
    }, [isOpen, isReadOnly, noteValue, useUncontrolledInput, updateCharacterCounter]);

    const handleSave = async () => {
        if (isReadOnly) return;

        if (useUncontrolledInput) {
            // Uncontrolled input: read from ref and compare with original
            const newValue = noteInputRef.current?.value || '';

            // Validate length
            if (newValue.length > MAX_NOTE_LENGTH) {
                // This shouldn't happen due to maxLength, but just in case
                return;
            }

            if (originalValue !== undefined && newValue !== originalValue) {
                await onSave(newValue);
            } else if (originalValue === undefined) {
                // If no originalValue provided, always save
                await onSave(newValue);
            } else {
                // No changes, just close
                onClose();
            }
        } else {
            // Controlled input: use the current noteValue prop
            // Validate length
            if (noteValue.length > MAX_NOTE_LENGTH) {
                // This shouldn't happen due to maxLength, but just in case
                return;
            }
            await onSave(noteValue);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            if (!isReadOnly) {
                handleSave();
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (!isReadOnly) {
            // Update counter immediately via DOM (no state update)
            updateCharacterCounter(e.target.value.length);

            if (onChange) {
                onChange(e.target.value);
            }
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        if (isReadOnly) return;

        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');

        if (useUncontrolledInput) {
            // For uncontrolled input, get current value and calculate new value
            const textarea = noteInputRef.current;
            if (!textarea) return;

            const currentValue = textarea.value;
            const selectionStart = textarea.selectionStart;
            const selectionEnd = textarea.selectionEnd;

            // Insert pasted text at cursor position
            const newValue =
                currentValue.substring(0, selectionStart) +
                pastedText +
                currentValue.substring(selectionEnd);

            // Truncate if necessary
            if (newValue.length > MAX_NOTE_LENGTH) {
                const truncatedValue = newValue.substring(0, MAX_NOTE_LENGTH);
                const truncatedChars = newValue.length - MAX_NOTE_LENGTH;

                // Set the truncated value
                textarea.value = truncatedValue;

                // Update cursor position
                const newCursorPos = Math.min(selectionStart + pastedText.length - truncatedChars, truncatedValue.length);
                textarea.setSelectionRange(newCursorPos, newCursorPos);

                // Update character count directly
                updateCharacterCounter(truncatedValue.length);

                // Show warning
                setPasteWarning(`Pasted text was truncated. ${truncatedChars.toLocaleString()} character${truncatedChars !== 1 ? 's' : ''} were removed.`);
                setTimeout(() => setPasteWarning(null), 5000);
            } else {
                // No truncation needed
                textarea.value = newValue;
                const newCursorPos = selectionStart + pastedText.length;
                textarea.setSelectionRange(newCursorPos, newCursorPos);
                updateCharacterCounter(newValue.length);
            }
        } else {
            // For controlled input, use onChange handler
            const textarea = noteInputRef.current;
            if (!textarea || !onChange) return;

            const currentValue = noteValue;
            const selectionStart = textarea.selectionStart;
            const selectionEnd = textarea.selectionEnd;

            // Insert pasted text at cursor position
            const newValue =
                currentValue.substring(0, selectionStart) +
                pastedText +
                currentValue.substring(selectionEnd);

            // Truncate if necessary
            if (newValue.length > MAX_NOTE_LENGTH) {
                const truncatedValue = newValue.substring(0, MAX_NOTE_LENGTH);
                const truncatedChars = newValue.length - MAX_NOTE_LENGTH;

                // Update counter immediately (before onChange triggers)
                updateCharacterCounter(truncatedValue.length);
                onChange(truncatedValue);

                // Update cursor position after state update
                setTimeout(() => {
                    if (textarea) {
                        const newCursorPos = Math.min(selectionStart + pastedText.length - truncatedChars, truncatedValue.length);
                        textarea.setSelectionRange(newCursorPos, newCursorPos);
                    }
                }, 0);

                // Show warning
                setPasteWarning(`Pasted text was truncated. ${truncatedChars.toLocaleString()} character${truncatedChars !== 1 ? 's' : ''} were removed.`);
                setTimeout(() => setPasteWarning(null), 5000);
            } else {
                // Update counter immediately (before onChange triggers)
                updateCharacterCounter(newValue.length);
                onChange(newValue);

                // Update cursor position after state update
                setTimeout(() => {
                    if (textarea) {
                        const newCursorPos = selectionStart + pastedText.length;
                        textarea.setSelectionRange(newCursorPos, newCursorPos);
                    }
                }, 0);
            }
        }
    };

    if (!isOpen) return null;

    const handleMouseDown = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        // Track if mousedown happened outside the modal content
        if (!target.closest('[data-note-modal]') &&
            target.tagName !== 'TEXTAREA' &&
            !target.closest('textarea')) {
            mouseDownOutsideRef.current = true;
        } else {
            mouseDownOutsideRef.current = false;
        }
    };

    const handleMouseUp = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        // Only close if both mousedown and mouseup happened outside the modal
        if (!target.closest('[data-note-modal]') &&
            target.tagName !== 'TEXTAREA' &&
            !target.closest('textarea') &&
            mouseDownOutsideRef.current) {
            onClose();
        }
        // Reset the flag
        mouseDownOutsideRef.current = false;
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
            }}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
        >
            <div
                style={{
                    backgroundColor: theme.colors.surface,
                    borderRadius: theme.borderRadius.lg,
                    padding: '0',
                    width: '50vw',
                    height: '50vh',
                    overflow: 'hidden',
                    boxShadow: theme.shadows.lg,
                    display: 'flex',
                    flexDirection: 'column',
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                data-note-modal
            >
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: `${theme.spacing.lg} ${theme.spacing.lg}`,
                    borderBottom: `1px solid ${theme.colors.border.default}`,
                    backgroundColor: theme.colors.primary,
                }}>
                    <h2 style={{
                        margin: 0,
                        fontSize: theme.typography.fontSize.xl,
                        fontWeight: theme.typography.fontWeight.semibold,
                        color: theme.colors.text.inverse,
                    }}>
                        {isReadOnly ? 'View Case Note' : 'Edit Case Note'}
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: theme.typography.fontSize.xxl,
                            color: theme.colors.text.inverse,
                            cursor: 'pointer',
                            padding: '0',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: theme.borderRadius.sm,
                        }}
                    >
                        ×
                    </button>
                </div>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    padding: theme.spacing.lg,
                    overflow: 'hidden',
                }}>
                    {useUncontrolledInput ? (
                        <textarea
                            ref={noteInputRef}
                            defaultValue={noteValue}
                            readOnly={isReadOnly}
                            onKeyDown={handleKeyDown}
                            onPaste={handlePaste}
                            onChange={(e) => updateCharacterCounter(e.target.value.length)}
                            maxLength={MAX_NOTE_LENGTH}
                            style={{
                                width: '100%',
                                flex: 1,
                                padding: theme.spacing.md,
                                border: `1px solid ${theme.colors.border.default}`,
                                borderRadius: theme.borderRadius.sm,
                                fontSize: theme.typography.fontSize.md,
                                backgroundColor: theme.colors.surface,
                                color: theme.colors.text.primary,
                                outline: 'none',
                                fontFamily: 'inherit',
                                resize: 'none',
                                marginBottom: theme.spacing.sm,
                                cursor: isReadOnly ? 'default' : 'text',
                                opacity: isReadOnly ? 0.9 : 1,
                            }}
                            placeholder="Enter case notes..."
                        />
                    ) : (
                        <textarea
                            ref={noteInputRef}
                            value={noteValue}
                            onChange={handleChange}
                            readOnly={isReadOnly}
                            onKeyDown={handleKeyDown}
                            onPaste={handlePaste}
                            maxLength={MAX_NOTE_LENGTH}
                            style={{
                                width: '100%',
                                flex: 1,
                                padding: theme.spacing.md,
                                border: `1px solid ${theme.colors.border.default}`,
                                borderRadius: theme.borderRadius.sm,
                                fontSize: theme.typography.fontSize.md,
                                backgroundColor: theme.colors.surface,
                                color: theme.colors.text.primary,
                                outline: 'none',
                                fontFamily: 'inherit',
                                resize: 'none',
                                marginBottom: theme.spacing.sm,
                                cursor: isReadOnly ? 'default' : 'text',
                                opacity: isReadOnly ? 0.9 : 1,
                            }}
                            placeholder="Enter case notes..."
                        />
                    )}
                    {pasteWarning && (
                        <div style={{
                            fontSize: theme.typography.fontSize.sm,
                            color: '#f59e0b',
                            backgroundColor: '#fef3c7',
                            border: '1px solid #f59e0b',
                            borderRadius: theme.borderRadius.sm,
                            padding: theme.spacing.sm,
                            marginBottom: theme.spacing.sm,
                            textAlign: 'center',
                        }}>
                            {pasteWarning}
                        </div>
                    )}
                    {!isReadOnly && (
                        <div style={{
                            fontSize: theme.typography.fontSize.sm,
                            color: theme.colors.text.secondary,
                            textAlign: 'right',
                            marginBottom: theme.spacing.md,
                        }}>
                            <span ref={counterRef} />
                        </div>
                    )}
                    <div style={{
                        display: 'flex',
                        justifyContent: isReadOnly ? 'space-between' : 'flex-end',
                        alignItems: 'center',
                        gap: theme.spacing.md,
                    }}>
                        {isReadOnly && (
                            <span style={{
                                fontSize: theme.typography.fontSize.sm,
                                color: theme.colors.text.secondary,
                                fontStyle: 'italic',
                            }}>
                                To edit this note, move this case to Working status
                            </span>
                        )}
                        <div style={{ display: 'flex', gap: theme.spacing.md }}>
                            {!isReadOnly && (
                                <button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    style={{
                                        padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                                        border: `1px solid ${theme.colors.primary}`,
                                        borderRadius: theme.borderRadius.sm,
                                        backgroundColor: theme.colors.primary,
                                        color: theme.colors.text.inverse,
                                        fontSize: theme.typography.fontSize.md,
                                        fontWeight: theme.typography.fontWeight.medium,
                                        cursor: isSaving ? 'wait' : 'pointer',
                                        opacity: isSaving ? 0.6 : 1,
                                        transition: `all ${theme.transitions.normal}`,
                                    }}
                                >
                                    {isSaving ? 'Applying...' : 'Apply'}
                                </button>
                            )}
                            <button
                                onClick={onClose}
                                style={{
                                    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                                    border: `1px solid ${theme.colors.border.default}`,
                                    borderRadius: theme.borderRadius.sm,
                                    backgroundColor: theme.colors.surface,
                                    color: theme.colors.text.primary,
                                    fontSize: theme.typography.fontSize.md,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    cursor: 'pointer',
                                    transition: `all ${theme.transitions.normal}`,
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

